import { checkoutAuthHeader } from './api';
import { parseWebEnv } from './env';
import { getMetaContext, getOrCreateVisitorId } from './meta-cookies';
import { ONBOARDING_GATE_KEY, LEGACY_SEEN_KEY } from './onboarding-gate';
import { trackLead } from './tracking';
import type { Locale } from '../i18n/index';

function getApiUrl(): string {
  try {
    return parseWebEnv(import.meta.env).VITE_API_URL;
  } catch {
    // In test environments import.meta.env may not be fully set; fall back gracefully.
    const raw = (import.meta.env as Record<string, string | undefined>).VITE_API_URL;
    return raw ?? '';
  }
}

const API_URL = getApiUrl();

export interface User {
  id: string;
  email: string;
  name?: string | null;
  tin?: string | null;
  /** Contact phone (E.164 digits), collected after payment. */
  phone?: string | null;
  /** Whether the bot can deliver Telegram alerts to this user. */
  telegramConnected?: boolean;
  /** Profile photo URL (from Telegram login), or null. */
  avatarUrl?: string | null;
  /** Master on/off switch for all saved-search + supplier-match alerts. */
  notificationsEnabled?: boolean;
  /** Per-channel alert toggles (master = email || telegram). */
  emailNotificationsEnabled?: boolean;
  telegramNotificationsEnabled?: boolean;
  /** Persisted UI language (uz|ru) — the server localizes alerts to match it. */
  locale?: string;
  /** Staff / test account: analytics (Meta Pixel, PostHog, GA) stay quiet. */
  internal?: boolean;
  /**
   * Whether this account has ever redeemed a login code (soft-signup, ADR
   * 2026-09-07). Access never depends on it — only outbound marketing email
   * (alerts/digests) does, server-side. `undefined` on a mocked/partial user
   * object must NOT be treated as "verified"; UI gating checks `=== false`.
   */
  emailVerified?: boolean;
  /**
   * Active paid subscription, or null/absent when on the free plan. Set by
   * GET /auth/me so the UI can reflect the real plan instead of always "free".
   */
  subscription?: {
    plan: string;
    status: string;
    currentPeriodEnd: string | null;
    expired: boolean;
    /** Whether auto-renewal is on (charged via the saved card-on-file). */
    autoRenew?: boolean;
    /**
     * What the next auto-renewal charges, in so'm (ADR 0014 grandfathered
     * price; null on rows from before the lock → fall back to the plan price).
     * Optional until every session payload carries it.
     */
    lockedPriceUzs?: number | null;
    /**
     * Where this entitlement comes from — the user's OWN purchase, or a seat
     * inherited from a team they joined. Entitlement does not follow team role
     * (a member who also pays for themselves resolves to `'own'`), so this is
     * the only honest signal for the «Доступ от команды» badge.
     */
    via: 'own' | 'team';
    /**
     * How the subscription came to be: `payment` (bought) or `grant` (comped by
     * an admin). The card-on-file ask must only target real payers — attaching
     * a card to a grant would have the renewal charger bill a subscription
     * nobody bought. Optional until every session payload carries it.
     */
    source?: 'payment' | 'grant';
    /** The team's display name when `via === 'team'`; null otherwise. */
    teamName: string | null;
    /**
     * True when the plan allows an unlimited number of saved searches
     * (`categoryLimit === null` server-side — Pro/ИП/Бизнес/test). The free tier
     * is capped (see `User.savedSearchLimit`); an unlimited user must always be
     * offered "create new" even while they already own alerts.
     */
    unlimitedSavedSearches?: boolean;
  } | null;
  /**
   * «Пауза на 1 месяц» (2026-10-09): the user's OWN subscription has a
   * scheduled or running pause. While it runs `subscription` is null (Pro is
   * closed), so this is what lets the cabinet still show «Пауза до …» and
   * «Возобновить сейчас». ISO strings; absent/null → no pause.
   */
  subscriptionPause?: { plan: string; startsAt: string; until: string } | null;
  /**
   * Closed-beta feature keys this account holds — `GET /auth/me` sends the
   * array (empty when none), server-side `feature_grants` plus anything the API
   * has opened to everyone. Read it through `useFeatureAccess`, never directly.
   *
   * ⚠️ Nothing to do with the PostHog experiment flags in `use-feature-flag.ts`:
   * those are A/B assignment and arrive asynchronously in the browser; this is
   * an access decision made by the server and it is part of the session payload.
   *
   * Optional only because mocked/partial `User` objects in tests and older
   * cached sessions predate it — absent must read as "no features", which is
   * what `useFeatureAccess` does.
   */
  features?: string[];
  /**
   * Closed-beta feature keys this account has ASKED for — the waitlist, the
   * other side of `features`. `GET /auth/me` sends the array (empty when none).
   * Since 2026-09-29 a key here WITHOUT the matching `featureWaitlistAnswered`
   * entry is a press from before the questionnaire: the button offers the form
   * again («Дополните анкету»). Read both through `useFeatureWaitlistState`.
   *
   * Optional for the same reason as `features`: absent reads as "not queued".
   */
  featureWaitlist?: string[];
  /**
   * The subset of `featureWaitlist` whose questionnaire is SUBMITTED — what
   * «Вы в очереди» means since 2026-09-29. Optional: a payload from an API
   * that predates the field reads as "nothing answered", which offers the form
   * — never the other way round.
   */
  featureWaitlistAnswered?: string[];
  /**
   * How many ACTIVE saved searches this account may keep — `null` = unlimited
   * (Pro / ИП / Бизнес), a number = the free cap (3 since 2026-10-04, was 1).
   * `GET /auth/me` derives it from the SAME rule that enforces the cap
   * (`savedSearchLimitFor` in the API), so the web never hard-codes the number.
   * Read it through `savedSearchCapFor` (`lib/saved-search-cap.ts`).
   *
   * Optional: a payload from an API that predates the field is enforcing the
   * old cap of one, and `savedSearchCapFor` reads absent as exactly that.
   */
  savedSearchLimit?: number | null;
  createdAt: string;
}

interface ApiEnvelope<T> {
  data: T;
}

interface ApiErrors {
  errors: Array<{ code: string; message: string; field?: string }>;
}

/** Sentinel so me() can detect a clean 401 without an error body. */
export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

/** Carries the API's machine-readable `code` so callers can localize/branch
 *  (e.g. `telegram_account_taken`) instead of showing the raw server message. */
export class AuthApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    /** Which input the API blamed, when it said. Needed because
     *  `validation_error` is a whole CLASS of rejection — the code alone cannot
     *  tell «this address has no TLD» from any other field's complaint, and a
     *  message is untranslated server prose we must never show. */
    readonly field?: string,
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  /** Optional peek at the raw Response, for callers that need headers. */
  onResponse?: (res: Response) => void,
): Promise<T> {
  // Carry the anonymous visitor id so `AuthController.stitchVisitor` can link the
  // pre-signup `anon_id` to the new user id on register/login/telegram. This
  // module builds its own headers (it predates `api.ts`), so it did NOT send the
  // header `api.ts:makeHeaders` sends — and since the stitch is fire-and-forget,
  // every auth call silently skipped it and `visitor_links` stayed empty. Keep
  // this in sync with `api.ts`.
  const vid = getOrCreateVisitorId();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(vid ? { 'x-anon-id': vid } : {}),
      // The checkout token, on the ONE auth path allowed to carry it
      // (`/auth/session/adopt`) and only while no real session exists. `{}`
      // everywhere else, so every other call in this module sends exactly the
      // headers it always sent. See `api.ts:checkoutAuthHeader`.
      ...checkoutAuthHeader(path),
      ...(options.headers ?? {}),
    },
  });

  onResponse?.(res);

  if (!res.ok) {
    // Try to parse error envelope
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      if (res.status === 401) throw new UnauthorizedError();
      throw new Error(`HTTP ${res.status}`);
    }
    const errBody = body as ApiErrors;
    // A 401 whose only content is the framework's generic envelope
    // (`{"errors":[{"code":"http_error","message":"Unauthorized"}]}`) is a CLEAN
    // "not signed in", not an API refusal with a code to branch on. It used to
    // fall through to AuthApiError, so auth-context treated every anonymous
    // page load as a transient failure: three /auth/me calls with backoff
    // before the guest UI settled (measured 2026-09-07). Endpoint-specific
    // 401 codes (wrong password, expired link) still surface as AuthApiError.
    const generic =
      !errBody?.errors?.length ||
      (errBody.errors[0].code === 'http_error' &&
        /^unauthorized$/i.test(errBody.errors[0].message));
    if (res.status === 401 && generic) {
      throw new UnauthorizedError();
    }
    if (errBody?.errors?.length) {
      throw new AuthApiError(
        errBody.errors[0].message,
        errBody.errors[0].code,
        errBody.errors[0].field,
      );
    }
    if (res.status === 401) throw new UnauthorizedError();
    throw new Error(`HTTP ${res.status}`);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return undefined as T;
  }

  return (body as ApiEnvelope<T>).data;
}

interface AuthResult {
  accessToken: string;
  user: User;
  /**
   * True only when this call created the account (first-ever sign-in). Drives
   * the client Pixel CompleteRegistration so returning logins don't over-count.
   */
  isNew: boolean;
}

export async function register(params: {
  email: string;
  password: string;
  name?: string;
  /** Uzbek ИНН/СТИР (9 digits) — optional at signup. */
  tin?: string;
  /** Shared dedup id for the Pixel ↔ CAPI CompleteRegistration pair. */
  metaEventId?: string;
}): Promise<AuthResult> {
  return request<AuthResult>('/auth/register', {
    method: 'POST',
    // Attach Meta tracking context (Pixel cookies, UTM, source URL) so the
    // server-side CAPI can fire CompleteRegistration with the same signals.
    body: JSON.stringify({ ...params, ...getMetaContext() }),
  });
}

/**
 * Ask for an email login code (ADR 0008).
 *
 * Resolves for ANY address — unknown, rate-limited, unmailable. The server
 * deliberately gives one answer so the endpoint cannot be used to test which
 * companies have accounts here, and the UI must not try to be more helpful than
 * that: "if that address is valid, a code is on its way" is the whole truth we
 * are willing to tell.
 */
/**
 * `POST /auth/method` — which credential should the login page ask for?
 *
 * `false` covers BOTH "account has no password" and "no such account", which is
 * what stops the identifier-first flow becoming an existence probe: an OTP user,
 * a Telegram user and a stranger are indistinguishable here.
 */
export async function authMethod(email: string): Promise<{ hasPassword: boolean }> {
  return request<{ hasPassword: boolean }>('/auth/method', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export interface StartResult {
  status: 'signed_in' | 'code_required' | 'password_required';
  /** True only when THIS call created the account. Drives the browser Pixel
   *  leg of CompleteRegistration — the server fires the CAPI leg for the same
   *  call, and both carry the `metaEventId` below so Meta dedupes them into
   *  one conversion. A returning soft account also answers `signed_in`, so the
   *  status alone would count it again on every visit. */
  isNew: boolean;
  /**
   * Present ONLY for `silent: true` + `signed_in`. The session the server did
   * NOT write into a cookie: the browser holds it in memory for `/billing/*`
   * and trades it at `POST /auth/session/adopt` once the payment succeeds
   * (`api.ts:setCheckoutToken`). Absent in every non-silent response, which is
   * byte-identical to what this endpoint always returned.
   */
  accessToken?: string;
}

/**
 * `POST /auth/start` — soft-signup entry point (ADR 2026-09-07).
 *
 * A brand-new or soft (unverified, passwordless) address gets an account AND
 * a session immediately (`signed_in`, httpOnly cookie set server-side); a
 * verified or password address is routed to whichever proof it needs
 * (`code_required` / `password_required`). This is now the first-touch
 * attribution moment — the account can be created on the very first email
 * submit — so it MUST carry `getMetaContext()` exactly like
 * `register`/`login`/`verifyOtp` do. Do NOT copy `authMethod`'s body (`{email}`
 * only): that would make attribution capture at `/auth/start` empty, which is
 * the whole point of moving it here.
 */
export async function start(
  email: string,
  ctx: {
    name?: string;
    phone?: string;
    locale?: string;
    metaEventId?: string;
    /**
     * «Гость до подтверждения оплаты» (2026-09-20). With `true` the server sets
     * NO session cookie and mails no best-effort verify code on `signed_in`,
     * and returns `accessToken` in the body instead. Everything that is a FACT
     * about account creation — the visitor stitch, the click id, first-touch
     * attribution, CompleteRegistration for `isNew` — still happens.
     * `code_required` / `password_required` are unchanged in both modes.
     */
    silent?: boolean;
  } = {},
): Promise<StartResult> {
  return request<StartResult>('/auth/start', {
    method: 'POST',
    body: JSON.stringify({ email, ...ctx, ...getMetaContext() }),
  });
}

export async function requestOtp(params: { email: string; locale?: string }): Promise<void> {
  await request<void>(
    '/auth/otp/request',
    { method: 'POST', body: JSON.stringify(params) },
    // Local dev builds print the login code to the console.
    //
    // With no Postmark token the API runs `StubEmailClient`, so the mail is
    // never actually sent and the code lives only in the server log — which
    // made the primary signup path untestable without leaving the page to grep
    // a terminal. The API echoes it in `X-Dev-Otp-Code`, and ONLY when it can
    // prove no real mail went out (see AuthController.requestOtp).
    //
    // `import.meta.env.DEV` is a literal at build time, so Vite eliminates this
    // whole branch from a production bundle rather than shipping it dormant.
    import.meta.env.DEV
      ? (res) => {
          const code = res.headers.get('X-Dev-Otp-Code');
          if (code) {
            console.info(
              `%c OTP %c ${code} %c  ${params.email} — dev only, no email was sent`,
              'background:#0f766e;color:#fff;font-weight:bold;border-radius:3px 0 0 3px',
              'background:#134e4a;color:#fff;font-weight:bold;font-size:14px',
              'color:#888',
            );
          }
        }
      : undefined,
  );
}

export interface VerifyOtpResult extends AuthResult {
  /**
   * True only when THIS call transitioned the account from unverified to
   * verified (server `AuthService.markEmailVerified`, gated by an
   * `emailVerifiedAt: null` guard). Post soft-signup `isNew` is usually
   * already false at verify time (the account was created at `/auth/start`),
   * so the browser CompleteRegistration/GA4 `sign_up` leg gates on THIS field,
   * not `isNew` — see `auth-context.tsx`.
   */
  firstVerification: boolean;
}

/** Redeem a code. A first redemption creates the account, so this is both login
 *  and signup — hence the Meta context, exactly as `register` carries it. */
export async function verifyOtp(params: {
  email: string;
  code: string;
  name?: string;
  /** Optional phone typed at signup; the server normalizes to E.164 or drops it. */
  phone?: string;
  locale?: string;
  metaEventId?: string;
}): Promise<VerifyOtpResult> {
  return request<VerifyOtpResult>('/auth/otp/verify', {
    method: 'POST',
    body: JSON.stringify({ ...params, ...getMetaContext() }),
  });
}

/** Password login. Carries the Meta context — not to fire an event (a login is
 *  neither a registration nor a purchase) but so the server can remember an ad
 *  click id. Registration by password is retired, so these accounts predate the
 *  OTP flow and a later ad click landing here is the only chance to learn one.
 *  Requires `LoginDto extends MetaTrackingDto`, or the API rejects the extra
 *  fields with a 400 — see the warning on that DTO. */
export async function login(params: { email: string; password: string }): Promise<AuthResult> {
  return request<AuthResult>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ ...params, ...getMetaContext() }),
  });
}

export function loginWithGoogle(): void {
  window.location.assign(`${API_URL}/auth/google`);
}

/** Telegram Login Widget payload (signed by Telegram, verified server-side). */
export interface TelegramAuthPayload {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export async function loginWithTelegram(
  payload: TelegramAuthPayload,
  metaEventId?: string,
): Promise<AuthResult> {
  return request<AuthResult>('/auth/telegram', {
    method: 'POST',
    // Meta context travels in a SEPARATE `meta` block — never flattened onto the
    // Telegram-signed fields, which would corrupt the server-side HMAC check.
    body: JSON.stringify({ ...payload, meta: { ...getMetaContext(), metaEventId } }),
  });
}

/**
 * `POST /auth/me/email/request` — mail a confirmation code to an address the
 * CURRENT account wants to adopt.
 *
 * For a Telegram-native account, whose address is the unmailable synthetic
 * `telegram_<id>@users.uztender.com`. Always resolves: whether the address
 * already has an account is exactly what the server must not disclose, so the
 * answer is the same either way and the next step decides.
 *
 * ⚠️ Deliberately NOT `/auth/start`. That route is public and session-blind, so
 * posting a new address there from a signed-in shell creates a THIRD account
 * and moves the cookie into it — the opposite of attaching an address.
 */
export async function requestEmailChange(params: {
  email: string;
  locale?: string;
}): Promise<void> {
  await request<void>('/auth/me/email/request', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export interface EmailChangeResult {
  user: User;
  /**
   * True when the address already had an account and THIS one was folded into
   * it — the session now belongs to the other account (the server re-issued the
   * cookie). The caller must re-read `/auth/me` rather than trust its cached
   * user, because the id has changed.
   */
  merged: boolean;
}

/** `POST /auth/me/email/verify` — redeem the code and adopt (or merge into)
 *  the address. Throws `AuthApiError` with `merge_refused` when the Telegram
 *  account carries a subscription that cannot be moved automatically. */
export async function verifyEmailChange(params: {
  email: string;
  code: string;
}): Promise<EmailChangeResult> {
  return request<EmailChangeResult>('/auth/me/email/verify', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * «Войти через Telegram» through the BOT — since 2026-10-18 the only Telegram
 * door on /login and /register, and the connect surfaces' fallback where the
 * widget cannot finish. See `components/telegram-bot-login.tsx` for the flow.
 */
export interface TelegramBotLoginRequest {
  /** The request's id, and the credential the poll presents. 32 base64url chars. */
  token: string;
  /** `login_<token>` — goes into `t.me/<bot>?start=…`. */
  startParam: string;
  /** How long the request accepts a Start (15 minutes). */
  expiresInS: number;
}

export type TelegramBotLoginStatus =
  | { status: 'pending'; opened: boolean }
  | { status: 'expired' | 'invalid' | 'consumed' }
  | { status: 'signed_in'; isNew: boolean; linked: boolean; user: User };

/**
 * The deploy window (2026-10-18). The web and the API ship in separate CI jobs,
 * and the API validates with `forbidNonWhitelisted`: while it is still on the
 * old code it answers 400 `validation_error` «property returnTo should not
 * exist» to the fields this release added (`returnTo` and `surface` on start,
 * `resumed` on the poll). Then the call is repeated in the OLD shape and the rest of this
 * page load stays on it — the sign-in works by the old path instead of dying
 * on a 400. The new fields are optional on the new API, so nothing else
 * changes once it is live.
 */
let legacyBotLoginApi = false;

function isUnknownFieldRefusal(err: unknown): boolean {
  return (
    err instanceof AuthApiError &&
    err.code === 'validation_error' &&
    /should not exist/.test(err.message)
  );
}

/** Tests only. */
export function resetBotLoginApiShape(): void {
  legacyBotLoginApi = false;
}

/** Send with the new fields; on an old API's «should not exist», once more without. */
async function withLegacyFallback<T>(
  hasNewFields: boolean,
  send: (withNewFields: boolean) => Promise<T>,
): Promise<T> {
  const modern = hasNewFields && !legacyBotLoginApi;
  try {
    return await send(modern);
  } catch (err) {
    if (!modern || !isUnknownFieldRefusal(err)) throw err;
    legacyBotLoginApi = true;
    return send(false);
  }
}

/**
 * `POST /auth/telegram/bot/start`. Sets an httpOnly cookie binding the request
 * to THIS browser — the exchange refuses any other — so it must go through
 * `request()` (credentials included), like every other auth call.
 */
export async function startTelegramBotLogin(params: {
  locale?: string;
  /** Shared with the browser Pixel leg of CompleteRegistration, if this
   *  sign-in turns out to create the account. */
  metaEventId?: string;
  /** Where the bot's magic link lands: a same-site path, locale prefix
   *  included. The server drops anything else. */
  returnTo?: string;
  /** Which button asked (`TelegramBotSurface`) — analytics only. */
  surface?: string;
}): Promise<TelegramBotLoginRequest> {
  return withLegacyFallback(!!params.returnTo || !!params.surface, (withNewFields) =>
    request<TelegramBotLoginRequest>('/auth/telegram/bot/start', {
      method: 'POST',
      // The Meta context travels NOW: the poll that completes a signup minutes
      // later carries none, so the server keeps this for its CAPI leg.
      body: JSON.stringify({
        locale: params.locale,
        ...(withNewFields && params.returnTo ? { returnTo: params.returnTo } : {}),
        ...(withNewFields && params.surface ? { surface: params.surface } : {}),
        meta: { ...getMetaContext(), metaEventId: params.metaEventId },
      }),
    }),
  );
}

/** `POST /auth/telegram/bot/status` — poll; on `signed_in` the session cookie
 *  (or the Telegram link) is already in place server-side. POST so the token
 *  never sits in a URL. `resumed` = the app root picked the request up from the
 *  pending cookie on a page load (analytics only). */
export async function pollTelegramBotLogin(
  token: string,
  opts: { resumed?: boolean } = {},
): Promise<TelegramBotLoginStatus> {
  return withLegacyFallback(opts.resumed === true, (withNewFields) =>
    request<TelegramBotLoginStatus>('/auth/telegram/bot/status', {
      method: 'POST',
      body: JSON.stringify(withNewFields ? { token, resumed: true } : { token }),
    }),
  );
}

export type TelegramMagicResult =
  | { status: 'expired' }
  | {
      status: 'signed_in';
      isNew: boolean;
      linked: boolean;
      user: User;
      /** Where to go now — a same-site path, locale prefix included. */
      returnTo: string;
      /** The Pixel leg of CompleteRegistration carries this when `isNew`. */
      metaEventId: string | null;
    };

/**
 * What the server would do with a magic link in THIS browser's session —
 * asked before anything is spent (security review, 18.10):
 * - `guest` — no session: ask «Войти как {firstName}?», redeem on the button;
 * - `link` — the account this connect link was made for: redeem now;
 * - `self` — the account that already owns this Telegram: redeem now;
 * - `mismatch` — any other account: «Эта ссылка для другого аккаунта», stop.
 */
export type TelegramMagicVerdict = 'guest' | 'link' | 'self' | 'mismatch';

export type TelegramMagicPeek =
  | { status: 'expired' }
  | { status: 'valid'; verdict: TelegramMagicVerdict; firstName: string | null };

/** `POST /auth/telegram/magic/peek` — read a magic link without spending it. */
export async function peekTelegramMagicLink(code: string): Promise<TelegramMagicPeek> {
  return request<TelegramMagicPeek>('/auth/telegram/magic/peek', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/**
 * `POST /auth/telegram/magic` — redeem the bot's one-time «Перейти на сайт»
 * link. Signs THIS browser in (a guest), or links the Telegram to the account
 * the link was made for; any other signed-in account gets 409
 * `telegram_link_mismatch` and nothing changes. `expired` covers an old, a
 * used and an unknown code alike.
 */
export async function redeemTelegramMagicLink(code: string): Promise<TelegramMagicResult> {
  return request<TelegramMagicResult>('/auth/telegram/magic', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** Link Telegram to the CURRENT account (enable alerts for an email/Google user). */
export async function connectTelegram(payload: TelegramAuthPayload): Promise<{ user: User }> {
  return request<{ user: User }>('/auth/me/telegram/connect', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Unlink Telegram from the CURRENT account — clears the stored id + chat link so
 *  the bot stops delivering and `telegramConnected` flips to false. */
export async function disconnectTelegram(): Promise<{ user: User }> {
  return request<{ user: User }>('/auth/me/telegram', { method: 'DELETE' });
}

/**
 * PATCH /auth/me/locale — persist the signed-in user's UI language (uz|ru) so
 * the server localizes saved-search alerts to match what they see in the app.
 */
export async function updateLocale(locale: Locale): Promise<{ user: User }> {
  return request<{ user: User }>('/auth/me/locale', {
    method: 'PATCH',
    body: JSON.stringify({ locale }),
  });
}

export interface MeResponse {
  user: User;
}

export async function me(): Promise<MeResponse | null> {
  try {
    return await request<MeResponse>('/auth/me');
  } catch (err) {
    if (err instanceof UnauthorizedError) {
      return null;
    }
    throw err;
  }
}

/**
 * `POST /auth/session/adopt` — turn the checkout token into a real session.
 *
 * Bearer-authenticated: the server reads the `Authorization` header (the JWT
 * guard tries Bearer before the cookie), writes that same token into the
 * session cookie, records `logged_in{method:'checkout'}` and answers with the
 * `GET /auth/me` body. The header is attached by the rule in `api.ts`, which is
 * why this takes no argument — the token is module state, not a parameter that
 * could be handed to the wrong call.
 *
 * 401 without a bearer, so this is never a way to "log in" a browser that holds
 * nothing.
 */
export async function adoptSession(): Promise<MeResponse['user']> {
  const res = await request<MeResponse>('/auth/session/adopt', { method: 'POST' });
  return res.user;
}

/**
 * Logout. The `tenders_session` cookie is httpOnly, so a client-side
 * `document.cookie` clear is a no-op — only the server can delete it. We POST to
 * `/auth/logout` so the cookie is actually cleared; otherwise it survives and
 * `me()` re-authenticates on the next page load. The JS clear stays as a
 * harmless best-effort for any non-httpOnly remnant.
 */
export async function logout(): Promise<void> {
  try {
    await request('/auth/logout', { method: 'POST' });
  } catch {
    // Best-effort — local state is still cleared by the caller.
  }
  document.cookie = 'tenders_session=; Max-Age=0; Path=/; SameSite=Lax';
  // Clear per-user UI state so the NEXT person to sign in on this browser starts
  // fresh (localStorage is per-browser, not per-user): onboarding shows again, the
  // Telegram nudge un-dismisses, and any stashed guest search is dropped.
  // ⚠️ Use the gate module's OWN key constants — hardcoding the string here is
  // exactly what broke this once: the gate migrated to `_v2` while this list kept
  // clearing the dead `_v1`, so the once/day throttle silently survived logout.
  try {
    [
      ONBOARDING_GATE_KEY,
      LEGACY_SEEN_KEY,
      'uztender_tg_banner_dismissed',
      'uztender_pending_saved_search',
    ].forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** What the post-signup company step found for a submitted ИНН (ADR 0008).
 *  Never carries a zero count — a branch that resolves to 0 falls through to
 *  the next one, and `none` renders no result at all. */
export type CompanyPayoff = {
  /** The Tax Committee positively REJECTED this ИНН. False when the registry
   *  was merely unreachable — a disabled key or a timeout must never be shown
   *  to a user as "your ИНН is wrong". */
  registryRejected?: boolean;
  /**
   * The ИНН's `suppliers` row already belongs to a DIFFERENT account
   * (`SupplierLinkService.link` throws `supplier_already_linked`, caught and
   * carried here rather than failing the whole submission — see
   * `CompanyStepService.submit`). The step still succeeds and the ИНН is
   * still stored; this only means the automatic supplier-profile link did
   * not happen. Callers must surface it — see `claim.conflict.title`.
   */
  supplierLinkConflict?: boolean;
} & (
  | { kind: 'wins'; count: number; companyName: string | null }
  | { kind: 'sector'; count: number; sectorName: string | null; companyName: string | null }
  | { kind: 'region'; count: number; regionName: string | null; companyName: string | null }
  | { kind: 'none'; companyName: string | null }
);

/** The company on file, resolved from the state registry. */
export interface CompanyOnFile {
  tin: string;
  name: string | null;
  fullName: string | null;
  status: string | null;
  activity: string | null;
  region: string | null;
  registryRejected: boolean;
}

/** `GET /auth/me/company` — what we resolved for the stored ИНН, or null when
 *  none is set. */
export async function fetchCompany(): Promise<CompanyOnFile | null> {
  return request<CompanyOnFile | null>('/auth/me/company');
}

/** One company the «Название или ИНН» field offers (`GET /auth/me/company/suggest`).
 *  `kind` is null for a registry company that has never appeared in an award. */
export interface CompanySuggestion {
  tin: string;
  name: string;
  region: string | null;
  /** Awards won in the last 24 months. */
  awards24m: number;
  kind: 'supplier' | 'buyer' | 'both' | null;
}

/** `GET /auth/me/company/suggest?q=` — up to eight registry companies for a
 *  name or an ИНН prefix. `[]` for anything too short to look up. */
export async function suggestCompanies(q: string): Promise<CompanySuggestion[]> {
  const rows = await request<CompanySuggestion[]>(
    `/auth/me/company/suggest?q=${encodeURIComponent(q)}`,
  );
  return Array.isArray(rows) ? rows : [];
}

/** How the ИНН reached the form — rides on `company_claim_submitted` as
 *  `meta.via`: `name` = picked from the suggestions, `tin` = typed. */
export type CompanyEntry = 'name' | 'tin';

/** `POST /auth/me/company` — submit the ИНН and get something worth the ask.
 *
 *  `via` travels as a QUERY parameter, not in the body: the API validates the
 *  body with `forbidNonWhitelisted`, so a body field would have had every
 *  submission refused by an API deployed before it. */
export async function submitCompany(tin: string, via?: CompanyEntry): Promise<CompanyPayoff> {
  const path = via ? `/auth/me/company?via=${via}` : '/auth/me/company';
  const payoff = await request<CompanyPayoff>(path, {
    method: 'POST',
    body: JSON.stringify({ tin }),
  });
  // GA4 `generate_lead`, fired HERE because this is the one browser point all
  // three ИНН forms pass through (onboarding, the standalone card, the claim
  // flow) — the server fires its Meta `Lead` on the same success. Analytics
  // must never be able to fail the ИНН step, hence the guard.
  try {
    trackLead();
  } catch {
    /* tracking unavailable — the ИНН is saved regardless. */
  }
  return payoff;
}

/** `POST /auth/me/phone` — contact phone, asked only after a successful payment. */
export async function submitPhone(phone: string): Promise<{ phone: string }> {
  return request<{ phone: string }>('/auth/me/phone', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
}
